/**
 * Wells Fargo credit-card application popup (financing page).
 * Self-contained: opens the WF application flow in a modal, mirroring the
 * Magento PWA financing page. Independent of the PDP paycalc component.
 * Credentials/URLs come from Theme settings via data-* on <wells-fargo-apply>.
 */
(function () {
  var scriptPromise = null;

  function ensureWellsFargoScript(src) {
    if (window.WF_SDK_BOOTSTRAP) return Promise.resolve();
    if (!scriptPromise) {
      scriptPromise = new Promise(function (resolve, reject) {
        var existing = document.querySelector('script[data-wells-fargo-sdk="1"]');
        if (existing) {
          if (window.WF_SDK_BOOTSTRAP) {
            resolve();
            return;
          }
          existing.addEventListener('load', function () { resolve(); }, {once: true});
          existing.addEventListener('error', function () {
            reject(new Error('Wells Fargo SDK failed'));
          }, {once: true});
          return;
        }
        var s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.dataset.wellsFargoSdk = '1';
        s.onload = function () { resolve(); };
        s.onerror = function () { reject(new Error('Wells Fargo SDK script failed to load')); };
        document.body.appendChild(s);
      });
    }
    return scriptPromise;
  }

  function readData(el, name) {
    if (!el) return '';
    var value = el.getAttribute('data-' + name);
    return value == null ? '' : String(value).trim();
  }

  function buildSecureConfigurationUrl(sessionTokenUrl, ip, useragent) {
    if (!sessionTokenUrl || !ip) return '';
    var ua = useragent || 'Mozilla';
    if (sessionTokenUrl.indexOf('session-token') >= 0) {
      var base = sessionTokenUrl.split('?')[0].replace(/\/$/, '');
      return base + '?ipaddress=' + encodeURIComponent(ip) + '&useragent=' + encodeURIComponent(ua);
    }
    var legacyPrefix = sessionTokenUrl.replace(/\/$/, '') + '/';
    return legacyPrefix + ip + '/useragent/' + (ua || 'browseruser');
  }

  function parseConfig(el) {
    return {
      sdkScriptUrl: readData(el, 'sdk-script-url'),
      gatewayEntityId: readData(el, 'gateway-entity-id'),
      merchantNumber: readData(el, 'merchant-number'),
      environment: readData(el, 'environment') || 'VALIDATION',
      sessionTokenUrl: readData(el, 'session-token-url'),
      sessionTokenUseragent: readData(el, 'session-token-useragent') || 'Mozilla',
      customerSupportPhone: readData(el, 'customer-support-phone') || '18884023475',
    };
  }

  function isConfigComplete(config) {
    return !!(config && config.sdkScriptUrl && config.gatewayEntityId && config.merchantNumber && config.sessionTokenUrl);
  }

  class WellsFargoApply extends HTMLElement {
    constructor() {
      super();
      this.config = null;
      this.initPromise = null;
      this.triggerSelector = '';
      this.onDocClick = null;
      this.onKeydown = null;
    }

    connectedCallback() {
      this.config = parseConfig(this);
      this.triggerSelector = readData(this, 'trigger-selector');

      this.overlayEl = this.querySelector('[data-wfa-overlay]');
      this.modalEl = this.querySelector('[data-wfa-modal]');
      this.closeBtn = this.querySelector('[data-wfa-close]');

      // Lift overlay/modal to <body> so ancestor overflow/transform can't clip them.
      if (this.overlayEl) document.body.appendChild(this.overlayEl);
      if (this.modalEl) document.body.appendChild(this.modalEl);

      var self = this;
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.close();
        });
      }
      if (this.overlayEl) {
        this.overlayEl.addEventListener('click', function () { self.close(); });
      }

      // Open on any external trigger matching the selector (event delegation so it
      // works regardless of where/when the trigger buttons render).
      if (this.triggerSelector) {
        this.onDocClick = function (e) {
          if (!e.target || !e.target.closest) return;
          var trigger = e.target.closest(self.triggerSelector);
          if (!trigger) return;
          e.preventDefault();
          self.open();
        };
        document.addEventListener('click', this.onDocClick);
      }

      this.onKeydown = function (e) {
        if (e.key === 'Escape') self.close();
      };
      document.addEventListener('keydown', this.onKeydown);

      // Eager init (like the PWA) so the application is rendered and ready on click.
      this.init();
    }

    disconnectedCallback() {
      if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.overlayEl && this.overlayEl.parentNode) this.overlayEl.parentNode.removeChild(this.overlayEl);
      if (this.modalEl && this.modalEl.parentNode) this.modalEl.parentNode.removeChild(this.modalEl);
      document.body.style.overflow = '';
    }

    init() {
      if (this.initPromise) return this.initPromise;
      if (!isConfigComplete(this.config)) return Promise.resolve();
      var self = this;
      this.initPromise = (async function () {
        var ipRes = await fetch('https://api.ipify.org/?format=json');
        var ipJson = await ipRes.json();
        var ip = ipJson && ipJson.ip;
        if (!ip) throw new Error('Could not resolve client IP');

        var secureConfigurationUrl = buildSecureConfigurationUrl(
          self.config.sessionTokenUrl, ip, self.config.sessionTokenUseragent
        );
        if (!secureConfigurationUrl) throw new Error('Could not build secureConfigurationUrl');

        window.WF_AO_SDK_Config = {
          gatewayEntityId: self.config.gatewayEntityId,
          secureConfigurationUrl: secureConfigurationUrl,
          targetDivId: 'wells_card_offer',
          displayPrequalification: false,
          displayApplication: true,
          merchantNumber: self.config.merchantNumber,
          customerSupportPhoneNumber: self.config.customerSupportPhone,
          environment: self.config.environment,
          loggingEnabled: false,
          consoleLoggingEnabled: false,
          displayPhoneBasedId: true,
        };

        await ensureWellsFargoScript(self.config.sdkScriptUrl);
        if (window.WF_SDK_BOOTSTRAP && !window.sdkHandler) {
          window.sdkHandler = window.WF_SDK_BOOTSTRAP.initialize();
        }
      })().catch(function (error) {
        self.initPromise = null;
        if (window.console && window.console.error) {
          window.console.error('Wells Fargo apply init failed:', error);
        }
      });
      return this.initPromise;
    }

    open() {
      var self = this;
      Promise.resolve(this.init()).then(function () {
        if (self.modalEl) self.modalEl.classList.add('is-open');
        if (self.overlayEl) self.overlayEl.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });
    }

    close() {
      if (this.modalEl) this.modalEl.classList.remove('is-open');
      if (this.overlayEl) this.overlayEl.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  if (!customElements.get('wells-fargo-apply')) {
    customElements.define('wells-fargo-apply', WellsFargoApply);
  }
})();
